import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Code, ArrowRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { CodeEditor } from '../../../components/ui/CodeEditor';
import type { TemplateFormData, OperationMapping, FieldMapping } from '../TemplateWizard';

interface OperationMappingStepProps {
  data: TemplateFormData;
  onChange: (updates: Partial<TemplateFormData>) => void;
  onValidate: (isValid: boolean) => void;
}

const httpMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'] as const;

const emptyOperation: OperationMapping = {
  name: '',
  description: '',
  endpoint: '',
  method: 'POST',
  requestMapping: [],
  responseMapping: [],
  headers: {},
};

const transformerOptions = [
  { value: '', label: 'Nenhum' },
  { value: 'toCents', label: 'Para Centavos' },
  { value: 'fromCents', label: 'De Centavos' },
  { value: 'toUpperCase', label: 'Maiúsculas' },
  { value: 'toLowerCase', label: 'Minúsculas' },
  { value: 'toISODate', label: 'Para ISO Date' },
  { value: 'fromISODate', label: 'De ISO Date' },
  { value: 'formatCPF', label: 'Formatar CPF' },
  { value: 'formatCNPJ', label: 'Formatar CNPJ' },
  { value: 'formatPhone', label: 'Formatar Telefone' },
  { value: 'base64Encode', label: 'Base64 Encode' },
  { value: 'base64Decode', label: 'Base64 Decode' },
];

export const OperationMappingStep: React.FC<OperationMappingStepProps> = ({
  data,
  onChange,
  onValidate,
}) => {
  const [expandedOp, setExpandedOp] = useState<number | null>(null);
  const [showJsonEditor, setShowJsonEditor] = useState<Record<number, boolean>>({});
  const operations = data.operations;

  // Add new operation
  const addOperation = () => {
    onChange({
      operations: [...operations, { ...emptyOperation }],
    });
    setExpandedOp(operations.length);
  };

  // Remove operation
  const removeOperation = (index: number) => {
    onChange({
      operations: operations.filter((_, i) => i !== index),
    });
    if (expandedOp === index) {
      setExpandedOp(null);
    }
  };

  // Update operation
  const updateOperation = (index: number, updates: Partial<OperationMapping>) => {
    const newOps = [...operations];
    newOps[index] = { ...newOps[index], ...updates };
    onChange({ operations: newOps });
  };

  // Add mapping to operation
  const addMapping = (opIndex: number, type: 'request' | 'response') => {
    const op = operations[opIndex];
    const key = type === 'request' ? 'requestMapping' : 'responseMapping';
    const newMapping: FieldMapping = { sourcePath: '', targetPath: '' };
    updateOperation(opIndex, {
      [key]: [...op[key], newMapping],
    });
  };

  // Update mapping
  const updateMapping = (
    opIndex: number,
    type: 'request' | 'response',
    mapIndex: number,
    updates: Partial<FieldMapping>
  ) => {
    const op = operations[opIndex];
    const key = type === 'request' ? 'requestMapping' : 'responseMapping';
    const newMappings = [...op[key]];
    newMappings[mapIndex] = { ...newMappings[mapIndex], ...updates };
    updateOperation(opIndex, { [key]: newMappings });
  };

  // Remove mapping
  const removeMapping = (opIndex: number, type: 'request' | 'response', mapIndex: number) => {
    const op = operations[opIndex];
    const key = type === 'request' ? 'requestMapping' : 'responseMapping';
    updateOperation(opIndex, {
      [key]: op[key].filter((_, i) => i !== mapIndex),
    });
  };

  // Validate on data change
  useEffect(() => {
    const isValid = operations.every(
      (op) => op.name.trim() && op.endpoint.trim()
    );
    onValidate(isValid || operations.length === 0);
  }, [operations, onValidate]);

  // Render mapping editor
  const renderMappingEditor = (
    opIndex: number,
    type: 'request' | 'response',
    mappings: FieldMapping[]
  ) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-surface-300">
          {type === 'request' ? 'Request Mapping' : 'Response Mapping'}
        </p>
        <button
          type="button"
          onClick={() => addMapping(opIndex, type)}
          className="text-xs text-primary-400 hover:text-primary-300"
        >
          + Adicionar
        </button>
      </div>

      {mappings.length === 0 ? (
        <p className="text-xs text-surface-500 italic">Nenhum mapeamento definido</p>
      ) : (
        <div className="space-y-2">
          {mappings.map((mapping, mapIndex) => (
            <div key={mapIndex} className="flex items-center gap-2 p-2 bg-surface-900 rounded">
              <input
                type="text"
                value={mapping.sourcePath}
                onChange={(e) => updateMapping(opIndex, type, mapIndex, { sourcePath: e.target.value })}
                placeholder={type === 'request' ? 'campo.origem' : 'response.campo'}
                className="flex-1 px-2 py-1 bg-surface-800 border border-surface-600 rounded text-white text-xs focus:outline-none focus:border-primary-500"
              />
              <ArrowRight className="w-4 h-4 text-surface-500 flex-shrink-0" />
              <input
                type="text"
                value={mapping.targetPath}
                onChange={(e) => updateMapping(opIndex, type, mapIndex, { targetPath: e.target.value })}
                placeholder={type === 'request' ? 'api.campo' : 'resultado.campo'}
                className="flex-1 px-2 py-1 bg-surface-800 border border-surface-600 rounded text-white text-xs focus:outline-none focus:border-primary-500"
              />
              <select
                value={mapping.transformer?.type || ''}
                onChange={(e) => updateMapping(opIndex, type, mapIndex, {
                  transformer: e.target.value ? { type: e.target.value } : undefined,
                })}
                className="px-2 py-1 bg-surface-800 border border-surface-600 rounded text-white text-xs focus:outline-none focus:border-primary-500"
              >
                {transformerOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeMapping(opIndex, type, mapIndex)}
                className="p-1 text-surface-400 hover:text-red-400"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Mapeamento de Operações</h2>
        <p className="text-sm text-surface-400">
          Defina as operações disponíveis e como os dados são mapeados entre o Hub e o provider
        </p>
      </div>

      {/* Operations List */}
      <div className="space-y-3">
        {operations.map((op, index) => (
          <div
            key={index}
            className="border border-surface-600 rounded-lg bg-surface-800/50 overflow-hidden"
          >
            {/* Operation Header */}
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-surface-700/50"
              onClick={() => setExpandedOp(expandedOp === index ? null : index)}
            >
              <div className={cn(
                'px-2 py-0.5 text-xs font-mono rounded',
                op.method === 'GET' && 'bg-green-500/20 text-green-400',
                op.method === 'POST' && 'bg-blue-500/20 text-blue-400',
                op.method === 'PUT' && 'bg-yellow-500/20 text-yellow-400',
                op.method === 'DELETE' && 'bg-red-500/20 text-red-400',
                op.method === 'PATCH' && 'bg-purple-500/20 text-purple-400',
              )}>
                {op.method}
              </div>
              
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-white truncate block">
                  {op.name || 'Operação sem nome'}
                </span>
                {op.endpoint && (
                  <span className="text-xs text-surface-500 truncate block font-mono">
                    {op.endpoint}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowJsonEditor((prev) => ({ ...prev, [index]: !prev[index] }));
                  }}
                  className={cn(
                    'p-1.5 rounded transition-colors',
                    showJsonEditor[index]
                      ? 'bg-primary-500/20 text-primary-400'
                      : 'text-surface-400 hover:text-white hover:bg-surface-700'
                  )}
                  title="Editor JSON"
                >
                  <Code className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeOperation(index); }}
                  className="p-1 text-surface-400 hover:text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {expandedOp === index ? (
                  <ChevronUp className="w-4 h-4 text-surface-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-surface-400" />
                )}
              </div>
            </div>

            {/* Operation Details (Expanded) */}
            {expandedOp === index && (
              <div className="px-4 pb-4 pt-2 border-t border-surface-700 space-y-4">
                {showJsonEditor[index] ? (
                  // JSON Editor Mode
                  <div className="space-y-2">
                    <p className="text-xs text-surface-400">Editar operação como JSON:</p>
                    <CodeEditor
                      value={JSON.stringify(op, null, 2)}
                      mode="json"
                      onChange={(value) => {
                        try {
                          const parsed = JSON.parse(value);
                          updateOperation(index, parsed);
                        } catch {
                          // Invalid JSON, ignore
                        }
                      }}
                      minHeight="200px"
                      maxHeight="400px"
                    />
                  </div>
                ) : (
                  // Form Mode
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Name */}
                      <div className="space-y-1.5">
                        <label className="block text-xs font-medium text-surface-300">
                          Nome da Operação <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={op.name}
                          onChange={(e) => updateOperation(index, { name: e.target.value })}
                          placeholder="createCharge"
                          className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                        />
                      </div>

                      {/* Method */}
                      <div className="space-y-1.5">
                        <label className="block text-xs font-medium text-surface-300">
                          Método HTTP
                        </label>
                        <select
                          value={op.method}
                          onChange={(e) => updateOperation(index, { method: e.target.value as any })}
                          className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                        >
                          {httpMethods.map((method) => (
                            <option key={method} value={method}>{method}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Endpoint */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-surface-300">
                        Endpoint <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={op.endpoint}
                        onChange={(e) => updateOperation(index, { endpoint: e.target.value })}
                        placeholder="/charge"
                        className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-primary-500"
                      />
                      <p className="text-xs text-surface-500">
                        Relativo à URL base. Use {'{param}'} para path params.
                      </p>
                    </div>

                    {/* Description */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-surface-300">
                        Descrição
                      </label>
                      <input
                        type="text"
                        value={op.description || ''}
                        onChange={(e) => updateOperation(index, { description: e.target.value })}
                        placeholder="Criar uma nova cobrança Pix"
                        className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                      />
                    </div>

                    {/* Request Mapping */}
                    <div className="p-3 bg-surface-900 rounded-lg">
                      {renderMappingEditor(index, 'request', op.requestMapping)}
                    </div>

                    {/* Response Mapping */}
                    <div className="p-3 bg-surface-900 rounded-lg">
                      {renderMappingEditor(index, 'response', op.responseMapping)}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Empty State */}
        {operations.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-surface-600 rounded-lg">
            <p className="text-surface-400 mb-2">Nenhuma operação definida</p>
            <p className="text-xs text-surface-500">
              Adicione operações como criar cobrança, consultar saldo, etc.
            </p>
          </div>
        )}
      </div>

      {/* Add Operation Button */}
      <button
        type="button"
        onClick={addOperation}
        className={cn(
          'flex items-center justify-center gap-2 w-full py-3 rounded-lg border-2 border-dashed transition-colors',
          'border-surface-600 text-surface-400 hover:border-primary-500 hover:text-primary-400'
        )}
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm font-medium">Adicionar Operação</span>
      </button>

      {/* Common Operations Templates */}
      <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
        <p className="text-xs font-medium text-surface-300 mb-3">Operações Comuns</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onChange({
              operations: [
                ...operations,
                {
                  name: 'createCharge',
                  description: 'Criar cobrança Pix',
                  endpoint: '/charge',
                  method: 'POST',
                  requestMapping: [
                    { sourcePath: 'amount', targetPath: 'value', transformer: { type: 'toCents' } },
                    { sourcePath: 'correlationId', targetPath: 'correlationID' },
                  ],
                  responseMapping: [
                    { sourcePath: 'charge.brCode', targetPath: 'qrCode' },
                    { sourcePath: 'charge.status', targetPath: 'status' },
                  ],
                  headers: {},
                },
              ],
            })}
            className="px-3 py-1.5 text-xs bg-surface-700 text-surface-300 rounded hover:bg-surface-600"
          >
            + Criar Cobrança
          </button>
          <button
            type="button"
            onClick={() => onChange({
              operations: [
                ...operations,
                {
                  name: 'getBalance',
                  description: 'Consultar saldo',
                  endpoint: '/balance',
                  method: 'GET',
                  requestMapping: [],
                  responseMapping: [
                    { sourcePath: 'balance', targetPath: 'available', transformer: { type: 'fromCents' } },
                  ],
                  headers: {},
                },
              ],
            })}
            className="px-3 py-1.5 text-xs bg-surface-700 text-surface-300 rounded hover:bg-surface-600"
          >
            + Consultar Saldo
          </button>
          <button
            type="button"
            onClick={() => onChange({
              operations: [
                ...operations,
                {
                  name: 'sendPayment',
                  description: 'Enviar Pix',
                  endpoint: '/payment',
                  method: 'POST',
                  requestMapping: [
                    { sourcePath: 'amount', targetPath: 'value', transformer: { type: 'toCents' } },
                    { sourcePath: 'pixKey', targetPath: 'destinationAlias' },
                    { sourcePath: 'pixKeyType', targetPath: 'destinationAliasType' },
                  ],
                  responseMapping: [
                    { sourcePath: 'payment.status', targetPath: 'status' },
                    { sourcePath: 'transaction.endToEndId', targetPath: 'endToEndId' },
                  ],
                  headers: {},
                },
              ],
            })}
            className="px-3 py-1.5 text-xs bg-surface-700 text-surface-300 rounded hover:bg-surface-600"
          >
            + Enviar Pix
          </button>
        </div>
      </div>
    </div>
  );
};

export default OperationMappingStep;
